import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { environment as env } from '../../environments/environment';
import { consumePendingAuth, redirectToHostedUi } from '../auth/oidc';

/**
 * /callback — Cognito redirects here after the user authenticates at the
 * Hosted UI. URL looks like /callback?code=<opaque>&state=<echoed>.
 *
 * Flow:
 *   1. Read `code` and `state` from the query string.
 *   2. Recover the pending PKCE attempt by state. Mismatch → CSRF, retry.
 *   3. POST { code, codeVerifier, redirectUri } to /rest/api/login-callback
 *      with credentials: 'include' so the backend can set httpOnly cookies.
 *   4. On success, navigate to the stored returnTo path.
 *   5. On failure, retry by redirecting back to Hosted UI.
 *
 * The token NEVER touches JS — the backend exchanges the code, sets
 * cookies, and returns just the user identity (which we don't need to
 * store here; the AuthService re-fetches /rest/api/session on demand).
 */
@Component({
  selector: 'app-callback',
  imports: [CommonModule],
  template: `
    <div class="grid place-items-center min-h-screen text-gray-300">
      <div class="text-center">
        <div class="text-sm" *ngIf="!error">Signing you in…</div>
        <div class="text-sm text-red-400" *ngIf="error">{{ error }}</div>
      </div>
    </div>
  `,
})
export class CallbackComponent implements OnInit {
  error: string | null = null;

  constructor(private http: HttpClient, private router: Router, private route: ActivatedRoute) {}

  async ngOnInit(): Promise<void> {
    const code = this.route.snapshot.queryParamMap.get('code');
    const state = this.route.snapshot.queryParamMap.get('state');
    const cognitoError = this.route.snapshot.queryParamMap.get('error');

    if (cognitoError) {
      this.error = `Cognito error: ${cognitoError}`;
      return;
    }
    if (!code || !state) {
      // Likely landed on /callback directly without going through the
      // Hosted UI — restart the flow.
      await redirectToHostedUi('/home');
      return;
    }
    const pending = consumePendingAuth(state);
    if (!pending) {
      this.error = 'Session expired or CSRF mismatch — restarting login…';
      setTimeout(() => redirectToHostedUi('/home'), 1500);
      return;
    }

    try {
      const body = {
        code,
        codeVerifier: pending.codeVerifier,
        redirectUri: pending.redirectUri,
      };
      const res: any = await this.http
        .post(`${env.apiUrl}/rest/api/login-callback`, body, { withCredentials: true })
        .toPromise();
      if (!res || !res.user) {
        throw new Error('login-callback returned no user');
      }
      // Replace URL so the ?code= doesn't sit in history.
      this.router.navigateByUrl(pending.returnTo || '/home', { replaceUrl: true });
    } catch (e: any) {
      this.error = `Login failed: ${e?.message || 'unknown error'}. Retrying…`;
      setTimeout(() => redirectToHostedUi('/home'), 2000);
    }
  }
}
